/**
 * ProductDetail.jsx - Product / Item detail page.
 *
 * Displays comprehensive information about a single item including:
 *   - Item detail card (key-value read-only fields)
 *   - Editable fields (criticality, shelf life, reorder point, spec notes)
 *   - Recommended source card (from sourcing evaluation)
 *   - All sources table (ranked vendor comparison)
 *   - Purchase history table (from item ledger entries / PO lines)
 *   - Price movements table
 *
 * Route: /products/:id
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Compass,
  DollarSign,
  Save,
  X,
  Star,
  ExternalLink,
} from "lucide-react";
import {
  DataTable,
  Badge,
  Loading,
  useToast,
} from "../components";
import { getItemById, updateItem } from "../api/products";
import { compareSourcesForItem } from "../api/sourcing";
import {
  getPurchaseOrders,
  getPOLinesByItem,
} from "../api/orders";
import { getVendorById } from "../api/vendors";
import { useCompany } from "../company/CompanyContext";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const CRITICALITY_OPTIONS = ["A", "B", "C"];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedCompany } = useCompany();

  /* Core data */
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Related data */
  const [sourcing, setSourcing] = useState(null);
  const [vendorMap, setVendorMap] = useState({});
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [poLines, setPoLines] = useState([]);

  /* Edit state */
  const [editFields, setEditFields] = useState({
    criticality: "",
    shelf_life_days: "",
    reorder_point: "",
    spec_notes: "",
  });
  const [editSaved, setEditSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef(null);

  /* ---------------------------------------------------------------- */
  /* Data fetching                                                     */
  /* ---------------------------------------------------------------- */

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Step 1: Load the item
      const itemData = await getItemById(id);
      setItem(itemData);

      // Initialize editable fields from item data (BC API v2.0 fields)
      setEditFields({
        criticality:
          itemData.criticality || "B",
        shelf_life_days: String(
          itemData.shelfLifeDays || ""
        ),
        reorder_point: String(
          itemData.reorderPoint || ""
        ),
        spec_notes: itemData.specNotes || "",
      });

      // Step 2: Load related data in parallel
      const companyFilter = selectedCompany?.id !== "all" ? selectedCompany?.name : undefined;
      const [sourcingResult, poLinesResult] = await Promise.allSettled([
        compareSourcesForItem(id, { company: companyFilter }),
        getPOLinesByItem(id),
      ]);

      // Process sourcing data
      if (sourcingResult.status === "fulfilled" && sourcingResult.value) {
        setSourcing(sourcingResult.value);
      }

      // Process PO lines for this item
      if (poLinesResult.status === "fulfilled") {
        const lines = poLinesResult.value || [];

        const relevantLines = lines.map((line) => ({
          ...line,
          _orderNo: line.orderNumber || line.poNumber,
          _vendorNo: line.vendorNumber,
          _orderDate: line.poOrderDate || line.orderDate,
          _receiptDate: line.receiptDate,
        }));

        setPoLines(relevantLines);

        // Build purchase history from PO lines
        const history = relevantLines.map((line) => ({
          po: line._orderNo,
          vendor: line._vendorNo,
          qty_ordered: line.quantity || 0,
          qty_received: line.quantityReceived || 0,
          unit_cost: line.unitCost || 0,
          order_date: line._orderDate,
          receipt_date: line._receiptDate,
        }));

        setPurchaseHistory(history);

        // Step 3: Fetch vendor details for all unique vendors
        const vendorNos = new Set();

        // From sourcing
        if (
          sourcingResult.status === "fulfilled" &&
          sourcingResult.value?.sources
        ) {
          sourcingResult.value.sources.forEach((s) =>
            vendorNos.add(s.vendorNo)
          );
        }

        // From PO lines
        relevantLines.forEach((line) => {
          if (line._vendorNo) vendorNos.add(line._vendorNo);
        });

        // Fetch vendor names
        const vendorEntries = await Promise.allSettled(
          [...vendorNos].map(async (vNo) => {
            try {
              const vendor = await getVendorById(vNo, [
                "number",
                "displayName",
              ]);
              return [vNo, vendor];
            } catch {
              return [vNo, { number: vNo, displayName: vNo }];
            }
          })
        );

        const vMap = {};
        vendorEntries.forEach((entry) => {
          if (entry.status === "fulfilled" && entry.value) {
            const [k, v] = entry.value;
            vMap[k] = v;
          }
        });
        setVendorMap(vMap);
      }
    } catch (err) {
      setError(err.message || "Failed to load item details");
      toast("Failed to load item details", "bad");
    } finally {
      setLoading(false);
    }
  }, [id, toast, selectedCompany]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ---------------------------------------------------------------- */
  /* Derived data                                                      */
  /* ---------------------------------------------------------------- */

  const sources = useMemo(() => {
    if (!sourcing?.sources) return [];

    return sourcing.sources.map((src) => ({
      rank: src.rank,
      vendor_no: src.vendorNo,
      vendor_name: vendorMap[src.vendorNo]?.displayName || src.vendorNo,
      currency: "",
      list_cost: src.avgUnitCost,
      landed_cost: src.avgUnitCost,
      vs_best:
        src.rank === 1
          ? 0
          : sourcing.sources[0]
            ? (
                ((src.avgUnitCost - sourcing.sources[0].avgUnitCost) /
                  sourcing.sources[0].avgUnitCost) *
                100
              ).toFixed(1)
            : 0,
      moq: "",
      lead_time: "",
      otif: src.scores?.otif,
      quality_score: src.scores?.quality,
      risk_score: src.scores?.risk,
      total_score: src.compositeScore,
      preferred: src.rank === 1,
    }));
  }, [sourcing, vendorMap]);

  const recommendation = useMemo(() => {
    if (!sourcing?.recommendation) return null;
    const rec = sourcing.recommendation;
    if (!rec.primaryVendor) return null;

    return {
      vendorNo: rec.primaryVendor.vendorNo,
      vendorName:
        vendorMap[rec.primaryVendor.vendorNo]?.displayName ||
        rec.primaryVendor.vendorNo,
      score: rec.primaryVendor.score,
      avgCost: rec.primaryVendor.avgUnitCost,
      type: rec.type,
      message: rec.message,
      secondaryVendor: rec.secondaryVendor
        ? {
            vendorNo: rec.secondaryVendor.vendorNo,
            vendorName:
              vendorMap[rec.secondaryVendor.vendorNo]?.displayName ||
              rec.secondaryVendor.vendorNo,
            score: rec.secondaryVendor.score,
          }
        : null,
    };
  }, [sourcing, vendorMap]);

  /* ---------------------------------------------------------------- */
  /* Edit handlers                                                     */
  /* ---------------------------------------------------------------- */

  const handleEditChange = useCallback((field, value) => {
    setEditFields((prev) => ({ ...prev, [field]: value }));
    setEditSaved(false);

    // Auto-save with debounce
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await updateItem(id, {
          criticality: field === "criticality" ? value : undefined,
          shelfLifeDays:
            field === "shelf_life_days" ? Number(value) || 0 : undefined,
          reorderPoint:
            field === "reorder_point" ? Number(value) || 0 : undefined,
          specNotes: field === "spec_notes" ? value : undefined,
        });
        setEditSaved(true);
        toast("Changes saved", "good");
      } catch (err) {
        toast("Failed to save: " + (err.message || "Unknown error"), "bad");
      } finally {
        setSaving(false);
      }
    }, 1200);
  }, [id, toast]);

  const handleSaveEdits = useCallback(async () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaving(true);
    try {
      await updateItem(id, {
        criticality: editFields.criticality,
        shelfLifeDays: Number(editFields.shelf_life_days) || 0,
        reorderPoint: Number(editFields.reorder_point) || 0,
        specNotes: editFields.spec_notes,
      });
      setEditSaved(true);
      toast("Changes saved", "good");
    } catch (err) {
      toast("Failed to save: " + (err.message || "Unknown error"), "bad");
    } finally {
      setSaving(false);
    }
  }, [id, editFields, toast]);

  const handleDiscardEdits = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (item) {
      setEditFields({
        criticality: item.criticality || "B",
        shelf_life_days: String(item.shelfLifeDays || ""),
        reorder_point: String(item.reorderPoint || ""),
        spec_notes: item.specNotes || "",
      });
      setEditSaved(true);
    }
  }, [item]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  /* ---------------------------------------------------------------- */
  /* Handlers                                                          */
  /* ---------------------------------------------------------------- */

  const handleAddPriceLine = useCallback(() => {
    toast("Price line feature coming soon", "default");
  }, [toast]);

  const handleExportPrices = useCallback(() => {
    if (sources.length === 0) {
      toast("No price data to export", "warn");
      return;
    }

    const headers = [
      "Rank",
      "Vendor No",
      "Vendor Name",
      "Landed Cost",
      "Total Score",
    ];
    const csvRows = [
      headers.join(","),
      ...sources.map((s) =>
        [
          s.rank,
          s.vendor_no,
          `"${(s.vendor_name || "").replace(/"/g, '""')}"`,
          s.landed_cost,
          s.total_score,
        ].join(",")
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `price-comparison-${id}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast("Price comparison exported", "good");
  }, [sources, id, toast]);

  /* ---------------------------------------------------------------- */
  /* Table columns                                                     */
  /* ---------------------------------------------------------------- */

  const sourcesColumns = useMemo(
    () => [
      { key: "rank", label: "Rank", type: "int" },
      { key: "vendor_no", label: "Vendor no." },
      { key: "vendor_name", label: "Vendor name" },
      { key: "currency", label: "Currency" },
      { key: "list_cost", label: "List cost", type: "money" },
      { key: "landed_cost", label: "Landed cost", type: "money" },
      {
        key: "vs_best",
        label: "vs Best%",
        render: (val) => {
          const n = Number(val);
          if (isNaN(n)) return "\u2014";
          const color =
            n === 0
              ? "var(--good)"
              : n <= 10
                ? "var(--warn)"
                : "var(--bad)";
          return (
            <span style={{ color, fontWeight: 600 }}>
              {n === 0 ? "base" : `+${n.toFixed(1)}%`}
            </span>
          );
        },
      },
      { key: "moq", label: "MOQ" },
      { key: "lead_time", label: "Lead time", type: "days" },
      { key: "otif", label: "OTIF", type: "pct" },
      { key: "quality_score", label: "Quality", type: "score" },
      { key: "risk_score", label: "Risk", type: "score" },
      { key: "total_score", label: "Total", type: "score" },
      {
        key: "preferred",
        label: "Preferred",
        render: (val) =>
          val ? (
            <Badge text="Preferred" variant="good" />
          ) : (
            <span style={{ color: "var(--ink-3)" }}>\u2014</span>
          ),
      },
    ],
    []
  );

  const purchaseHistoryColumns = useMemo(
    () => [
      { key: "po", label: "PO" },
      { key: "vendor", label: "Vendor" },
      { key: "qty_ordered", label: "Qty ordered", type: "int" },
      { key: "qty_received", label: "Qty received", type: "int" },
      { key: "unit_cost", label: "Unit cost", type: "money" },
      { key: "order_date", label: "Order date", type: "date" },
      { key: "receipt_date", label: "Receipt date", type: "date" },
    ],
    []
  );

  const priceMovementsColumns = useMemo(
    () => [
      { key: "when", label: "When", type: "date" },
      { key: "vendor", label: "Vendor" },
      { key: "was", label: "Was", type: "money" },
      { key: "now", label: "Now", type: "money" },
      { key: "source", label: "Source" },
    ],
    []
  );

  /* ---------------------------------------------------------------- */
  /* Detail rows helper                                                */
  /* ---------------------------------------------------------------- */

  const detailRows = useMemo(() => {
    if (!item) return [];

    const approvedSources = sources.length || "\u2014";

    return [
      { label: "Item number", value: item.number },
      { label: "Description", value: item.displayName || item.description },
      { label: "Base UOM", value: item.baseUnitOfMeasureCode },
      { label: "Category", value: item.itemCategoryCode },
      {
        label: "ERP unit cost",
        value: item.unitCost != null ? `PHP ${Number(item.unitCost).toFixed(2)}` : null,
      },
      {
        label: "Selling price",
        value: item.unitPrice != null ? `PHP ${Number(item.unitPrice).toFixed(2)}` : null,
      },
      {
        label: "Preferred vendor",
        value: recommendation?.vendorName || null,
      },
      { label: "Barcode", value: item.gtin || null },
      {
        label: "On hand",
        value:
          item.inventory != null
            ? Number(item.inventory).toLocaleString()
            : null,
      },
      {
        label: "Reorder point",
        value:
          item.reorderPoint != null
            ? Number(item.reorderPoint).toLocaleString()
            : null,
      },
      { label: "Approved sources", value: approvedSources },
    ];
  }, [item, sources, recommendation]);

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */

  if (loading) {
    return <Loading show text="Loading item details..." />;
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-body" style={{ color: "var(--bad)" }}>
          <p>{error}</p>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button className="btn" onClick={loadData}>
              Retry
            </button>
            <button className="btn ghost" onClick={() => navigate("/products")}>
              Back to Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="muted">Item not found.</p>
          <button
            className="btn"
            style={{ marginTop: 12 }}
            onClick={() => navigate("/products")}
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div style={styles.headerRow}>
        <div style={styles.headerLeft}>
          <button
            className="btn ghost"
            onClick={() => navigate("/products")}
          >
            <ArrowLeft size={15} /> Back
          </button>
          <div>
            <h1 style={{ marginTop: 8 }}>
              {item.displayName || item.description || item.number}
            </h1>
            <p className="muted" style={{ marginTop: 2 }}>
              {item.number}
              {item.itemCategoryCode && ` \u00B7 ${item.itemCategoryCode}`}
            </p>
          </div>
        </div>
        <div style={styles.headerBtns}>
          <button
            className="btn"
            onClick={() =>
              navigate(`/sourcing?item=${encodeURIComponent(id)}`)
            }
          >
            <Compass size={14} /> Open in sourcing adviser
          </button>
          <button className="btn primary" onClick={handleAddPriceLine}>
            <DollarSign size={14} /> Add price line
          </button>
        </div>
      </div>

      {/* Item Detail + Editable Fields */}
      <div style={styles.twoColGrid}>
        {/* Detail card */}
        <div className="card">
          <div className="card-head">
            <h3>Item Details</h3>
          </div>
          <div className="card-body">
            <div style={styles.detailGrid}>
              {detailRows.map((row) => (
                <div key={row.label} style={styles.detailItem}>
                  <span style={styles.detailLabel}>{row.label}</span>
                  <span style={styles.detailValue}>
                    {row.value ?? "\u2014"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Editable fields card */}
        <div className="card">
          <div className="card-head">
            <h3>Editable Fields</h3>
            <div style={{ display: "flex", gap: 6 }}>
              {!editSaved && (
                <>
                  <button
                    className="btn tiny"
                    onClick={handleSaveEdits}
                    disabled={saving}
                  >
                    <Save size={12} />{" "}
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button
                    className="btn tiny ghost"
                    onClick={handleDiscardEdits}
                    disabled={saving}
                  >
                    <X size={12} /> Discard
                  </button>
                </>
              )}
              {editSaved && (
                <Badge text="Saved" variant="good" />
              )}
            </div>
          </div>
          <div className="card-body">
            <div style={styles.editGrid}>
              <label style={styles.editLabel}>
                Criticality
                <select
                  value={editFields.criticality}
                  onChange={(e) =>
                    handleEditChange("criticality", e.target.value)
                  }
                  style={styles.editInput}
                >
                  {CRITICALITY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>

              <label style={styles.editLabel}>
                Shelf life (days)
                <input
                  type="number"
                  min="0"
                  value={editFields.shelf_life_days}
                  onChange={(e) =>
                    handleEditChange("shelf_life_days", e.target.value)
                  }
                  style={styles.editInput}
                  placeholder="0"
                />
              </label>

              <label style={styles.editLabel}>
                Reorder point
                <input
                  type="number"
                  min="0"
                  value={editFields.reorder_point}
                  onChange={(e) =>
                    handleEditChange("reorder_point", e.target.value)
                  }
                  style={styles.editInput}
                  placeholder="0"
                />
              </label>

              <label style={{ ...styles.editLabel, gridColumn: "1 / -1" }}>
                Spec notes
                <textarea
                  value={editFields.spec_notes}
                  onChange={(e) =>
                    handleEditChange("spec_notes", e.target.value)
                  }
                  style={{
                    ...styles.editInput,
                    resize: "vertical",
                    minHeight: 60,
                    fontFamily: "inherit",
                  }}
                  placeholder="Add specification notes..."
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Recommended Source */}
      {recommendation && (
        <div className="card">
          <div className="card-head">
            <h3>Recommended Source</h3>
            <Badge
              text={
                recommendation.type === "strong_single"
                  ? "Strong"
                  : recommendation.type === "recommended"
                    ? "Recommended"
                    : "Caution"
              }
              variant={
                recommendation.type === "strong_single"
                  ? "good"
                  : recommendation.type === "recommended"
                    ? "info"
                    : "warn"
              }
            />
          </div>
          <div className="card-body">
            <div style={styles.recGrid}>
              <div style={styles.recPrimary}>
                <div style={styles.recVendorRow}>
                  <Star
                    size={16}
                    style={{ color: "var(--warn)", flexShrink: 0 }}
                    fill="var(--warn)"
                  />
                  <span style={styles.recVendorName}>
                    {recommendation.vendorName}
                  </span>
                  <Badge text={recommendation.vendorNo} variant="accent" />
                </div>
                <p style={styles.recMessage}>
                  {recommendation.message}
                </p>
              </div>
              <div style={styles.recStats}>
                <div style={styles.recStat}>
                  <span style={styles.recStatLabel}>Score</span>
                  <span
                    style={{
                      ...styles.recStatValue,
                      color:
                        recommendation.score >= 80
                          ? "var(--good)"
                          : recommendation.score >= 60
                            ? "var(--warn)"
                            : "var(--bad)",
                    }}
                  >
                    {recommendation.score.toFixed(0)}/100
                  </span>
                </div>
                <div style={styles.recStat}>
                  <span style={styles.recStatLabel}>Avg cost</span>
                  <span style={styles.recStatValue}>
                    PHP {recommendation.avgCost.toFixed(2)}
                  </span>
                </div>
                {recommendation.secondaryVendor && (
                  <div style={styles.recStat}>
                    <span style={styles.recStatLabel}>Secondary</span>
                    <span style={styles.recStatValue}>
                      {recommendation.secondaryVendor.vendorName}
                      <span className="muted" style={{ marginLeft: 6 }}>
                        ({recommendation.secondaryVendor.score.toFixed(0)})
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Sources */}
      <div className="card">
        <div className="card-head">
          <h3>All Sources</h3>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn tiny" onClick={handleExportPrices}>
              Export
            </button>
            <button
              className="btn tiny"
              onClick={() =>
                navigate(`/sourcing?item=${encodeURIComponent(id)}`)
              }
            >
              <ExternalLink size={12} /> Adviser
            </button>
          </div>
        </div>
        <div className="card-body flush">
          <DataTable
            columns={sourcesColumns}
            rows={sources}
            emptyText="No sourcing data available. Run a sourcing evaluation to compare vendors."
          />
        </div>
      </div>

      {/* Purchase History */}
      <div className="card">
        <div className="card-head">
          <h3>Purchase History</h3>
          <span className="muted">
            {purchaseHistory.length} record
            {purchaseHistory.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="card-body flush">
          <DataTable
            columns={purchaseHistoryColumns}
            rows={purchaseHistory}
            emptyText="No purchase history found for this item"
          />
        </div>
      </div>

      {/* Price Movements */}
      <div className="card">
        <div className="card-head">
          <h3>Price Movements</h3>
        </div>
        <div className="card-body flush">
          <DataTable
            columns={priceMovementsColumns}
            rows={[]}
            emptyText="No price movement records available"
          />
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  headerLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },
  headerBtns: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  twoColGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 16,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px 20px",
  },
  detailItem: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 650,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "var(--ink-3)",
  },
  detailValue: {
    fontSize: 13.5,
    fontWeight: 550,
    color: "var(--ink)",
    wordBreak: "break-word",
  },
  editGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  editLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "var(--ink-3)",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  editInput: {
    padding: "7px 10px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    background: "var(--panel)",
    color: "var(--ink)",
    outline: "none",
    textTransform: "none",
    fontWeight: 500,
    letterSpacing: "0",
  },
  recGrid: {
    display: "flex",
    gap: 24,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  recPrimary: {
    flex: "1 1 300px",
  },
  recVendorRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  recVendorName: {
    fontSize: 15,
    fontWeight: 650,
    color: "var(--ink)",
  },
  recMessage: {
    fontSize: 13,
    color: "var(--ink-2)",
    lineHeight: 1.5,
  },
  recStats: {
    display: "flex",
    gap: 20,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  recStat: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 80,
  },
  recStatLabel: {
    fontSize: 10.5,
    fontWeight: 650,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--ink-3)",
  },
  recStatValue: {
    fontSize: 16,
    fontWeight: 680,
    color: "var(--ink)",
    fontVariantNumeric: "tabular-nums",
  },
};
